import React, { useReducer, createContext, useContext, ReactNode, Dispatch } from 'react';
import { App } from 'antd';
import globalStore from '@/store/global';

// 定义用户权限和登录状态类型
export type AuthStatus = {
    isLoggedIn: boolean;
    hasAuth: boolean;
    noAuthTip: string;
};

// 初始状态
const initialAuthStatus: AuthStatus = {
    isLoggedIn: false,
    hasAuth: false,
    noAuthTip: '',
};

// 定义动作类型
type AuthAction = { type: 'SET_AUTH_STATUS'; isLoggedIn: boolean; hasAuth: boolean; noAuthTip: string } | { type: 'RESET_AUTH_STATUS' };

// 定义 reducer 函数
function authReducer(state: AuthStatus, action: AuthAction): AuthStatus {
    switch (action.type) {
        case 'SET_AUTH_STATUS':
            return {
                ...state,
                isLoggedIn: action.isLoggedIn,
                hasAuth: action.hasAuth,
                noAuthTip: action.noAuthTip,
            };
        case 'RESET_AUTH_STATUS':
            return {
                ...initialAuthStatus,
            };
        default:
            throw new Error(`Unknown action type: ${(action as AuthAction).type}`);
    }
}

// 定义认证上下文类型
interface AuthContextData {
    authStatus: AuthStatus;
    dispatch: Dispatch<AuthAction>;
    withAuthCheck: (handler: Function, alwaysCall?: Function) => (...args: any[]) => void;
}

// 创建认证上下文
const AuthContext = createContext<AuthContextData | undefined>(undefined);

// 使用自定义 hook 封装 Context 消费
const useAuthContext = (): AuthContextData => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};

// AuthProvider 组件
interface AuthProviderProps {
    children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const { message } = App.useApp();
    const [authStatus, dispatch] = useReducer(authReducer, initialAuthStatus);

    // 定义一个用于处理登录判断的函数
    const withLoginCheck = () => {
        if (!authStatus.isLoggedIn) {
            globalStore.publish('main-app', {
                type: 'signIn',
            });
            return false;
        }
        return true;
    };

    // 定义 withAuthCheck 方法
    const withAuthCheck = (handler: Function, alwaysCall?: Function) => {
        return (...args: any[]) => {
            if (args[0] && typeof args[0].stopPropagation === 'function') {
                (args[0] as MouseEvent).stopPropagation();
            }

            // 先进行登录检查
            if (!withLoginCheck()) {
                return;
            }

            // 如果提供了 alwaysCall 方法，则进行调用
            if (alwaysCall) {
                alwaysCall(...args);
            }

            // 检查用户是否有权限
            if (!authStatus.hasAuth) {
                authStatus.noAuthTip && message.warning(authStatus.noAuthTip);
            } else {
                handler(...args);
            }
        };
    };

    return <AuthContext.Provider value={{ authStatus, dispatch, withAuthCheck }}>{children}</AuthContext.Provider>;
};

export { authReducer, initialAuthStatus, AuthContext, useAuthContext };

export default AuthProvider;
